<template>
  <a
    :title="fullName"
    aria-atomic="true"
    aria-live="polite"
    class="_login"
    href="javascript:void(0)"
  >
    <span class="far fa-user mr-1"></span>{{ fullName }}
    <span aria-hidden="true" class="fa fa-chevron-down ml-1"></span>
    <span id="jsLoginMessage" class="sr-only"
    >Du är nu inloggad på Organisationsregistret</span
    >
  </a>
  <div

    id="externalIdentity"
    v-click-outside="onClickOutside"
    class="externalIdentity externalIdentity-container externalIdentity-hide"
  >
    <div class="externalIdentity-close">
      <button
        id="externalIdentity-close--btn"
        :aria-expanded="state.ariaExpanded"
        aria-controls="externalIdentity"
        aria-label="Stäng"
        @click="closeMenu"
      >
        <svg
          aria-hidden="true"
          class="svg-inline--fa fa-times fa-w-11 fa-1x"
          data-icon="times"
          data-prefix="fas"
          focusable="false"
          role="img"
          viewBox="0 0 352 512"
          xmlns="http://www.w3.org/2000/svg"
        >
          <times-icon />
        </svg>
      </button>
    </div>
    <div class="externalIdentity-content">
      <span>{{ settings?.loginAsText }}</span>
      <p class="externalIdentity-user">{{ fullName }}</p>
      <div>
        <a
          :href="settings?.accountInfo.link"
          class="externalIdentity-user--button"
        >{{ settings?.accountInfo.text }}</a>
      </div>
      <hr class="externalIdentity-hr" />
      <a 
          class="externalIdentity-link" 
          :href="settings?.customerService.link"
        >{{ settings?.customerService.text }}<span class="external external-link"><i class="fas fa-external-link-alt"></i></span></a>
      <hr class="externalIdentity-hr" />
      <h5 v-if="settings?.otherServicesText" class="externalIdentity-h5">{{ settings?.otherServicesText }}</h5>
      <div v-if="settings?.digitalaSamlingen.link && settings?.digitalaSamlingen.text">
        <a 
          class="externalIdentity-link" 
          :href="settings?.digitalaSamlingen.link"
          >{{ settings?.digitalaSamlingen.text }}<span class="external external-link"><i class="fas fa-external-link-alt"></i></span></a>
      </div>
      <div v-if="settings?.webStore.link && settings?.webStore.text">
        <a 
          class="externalIdentity-link" 
          :href="settings?.webStore.link"
          >{{ settings?.webStore.text }}<span class="external external-link"><i class="fas fa-external-link-alt"></i></span></a>
      </div>
      <hr class="externalIdentity-hr" />
      <div class="externalIdentity-footer">
        <a 
          class="externalIdentity-link__small" 
          :href="settings?.privacy.link"
        >{{ settings?.privacy.text }}<span class="external external-link"><i class="fas fa-external-link-alt"></i></span></a>
        <a
          aria-atomic="true"
          aria-live="polite"
          class="externalIdentity-logout"
          href="/logout"
          title="Logga ut"
        >{{ settings?.logoutText }}</a>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { PropType, defineComponent, reactive } from "vue";
import External_linkIcon from "./svg/external_link-icon.vue";
import TimesIcon from "./svg/times-icon.vue";
import { useUserStore } from "@/stores/user";
import { storeToRefs } from "pinia";
import vClickOutside from "click-outside-vue3";
import { StartViewModel } from "@Shared/Models/StartViewModel";

export interface HeaderDropdownState {
  ariaExpanded: boolean;
}

export default defineComponent({
  components: {
    TimesIcon,
    External_linkIcon
  },
  props: {settings: Object as PropType<StartViewModel>},
  directives: {
    clickOutside: vClickOutside.directive
  },
  setup(props) {
    const userStore = useUserStore();
    const { getUser } = storeToRefs(userStore);
    const state = reactive<HeaderDropdownState>({
      ariaExpanded: false
    });

    function closeMenu(): void {
      document.querySelector("#externalIdentity")?.classList.toggle("externalIdentity-hide");
      state.ariaExpanded = !state.ariaExpanded;
    }

    if (userStore.isAuthenticated && !window.localStorage.getItem("loginMessage")) {
      window.localStorage.setItem("loginMessage", "true");
    }

    const fullName = `${getUser.value().firstName} ${getUser.value().lastName}`;

    function onClickOutside(event: any): void {
      if (event.target.className == "_login") {
        document
          .querySelector("#externalIdentity")
          ?.classList.toggle("externalIdentity-hide");
        state.ariaExpanded = !state.ariaExpanded;
      } else if (state.ariaExpanded) {
        closeMenu();
      }
    }

    return {
      state,
      fullName,
      onClickOutside,
      closeMenu,
      settings: props.settings
    };
  }
});
</script>
<style lang="scss" scoped>
a {
  display: block;
  text-decoration: none;
  font-size: 14px;
  font-weight: 400;
}

.external-link{
  display: inline-block;
  padding-left: 5px;
  padding-right: 5px;
  font-style: normal !important;
  font-variant: normal !important;
  font-weight: normal !important;
  text-transform: none !important;
  vertical-align: middle;
  opacity: .7;
}

.externalIdentity {
  font-size: 16px;
  font-family: Arial, Helvetica, sans-serif;
  box-sizing: border-box;
}

.externalIdentity-container {
  background-color: #fff;
  border-radius: 0.5em;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  color: #333;
  display: flex;
  flex-direction: column;
  padding: 2.1rem;
  position: absolute;
  right: 0;
  width: 23.75em;
  z-index: 100000;
}

.externalIdentity-close {
  position: absolute;
  right: 0;
  top: 0;
}

.externalIdentity-close button {
  background: none;
  border: none;
  width: 3rem;
  height: 3rem;
}

.externalIdentity-close button svg {
  width: 14px;
}

.externalIdentity-close button:focus {
  outline: 3px solid #0c5674;
  outline-offset: -3px;
}

.externalIdentity-user {
  font-size: 1.125rem;
  margin: 0.2rem 0 1.5rem;
}

.externalIdentity-user--button {
  background-color: #a7001f;
  border-radius: 3em;
  color: #fff !important;
  display: flex;
  height: 3em;
  justify-content: center;
  line-height: 3em;
  width: 100%;
  margin-bottom: 2rem;
  text-decoration: none;
}

.externalIdentity-user--button:hover {
  color: #fff;
  cursor: pointer;
  text-decoration: underline;
}

.externalIdentity-user--button:focus {
  outline: 3px solid #0c5674;
  outline-offset: -3px;
}

.externalIdentity-link {
  align-items: center;
  color: #00608a !important;
  display: inline-flex;
  height: auto !important;
  font-size: 1em !important;
  padding: 0 !important;
  text-decoration: none;
  margin: 0.2rem 0;
}

.externalIdentity-link:hover {
  background-color: inherit !important;
  color: #00608a !important;
  text-decoration: underline;
}

.externalIdentity-link:focus {
  outline: 3px solid #0c5674;
  outline-offset: -3px;
}

.externalIdentity-link__small {
  color: #00608a !important;
  font-size: 0.875em;
  padding: 0 !important;
  text-decoration: underline;
}

.externalIdentity-link__small:hover {
  background-color: inherit !important;
  color: #00608a !important;
  text-decoration: underline;
}

.externalIdentity-link__small:focus {
  outline: 3px solid #0c5674;
  outline-offset: -3px;
}

.externalIdentity-link svg {
  width: 0.625em;
  margin-left: 0.5em;
}

.externalIdentity-footer {
  align-items: center;
  display: flex;
}

.externalIdentity-logout {
  background-color: #746c52;
  border: 2px solid #666666;
  border-radius: 0.25em;
  color: #fff !important;
  font-size: 0.875em;
  margin-left: auto;
  padding: 0.5em 1em;
  text-decoration: none;
}

.externalIdentity-logout:hover {
  color: #fff;
  text-decoration: underline;
}

.externalIdentity-logout:focus {
  outline: 3px solid #0c5674;
  outline-offset: -3px;
}

.externalIdentity-hr {
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 0;
  border-top: 1px solid #e4e6dd;
}

.externalIdentity-hide {
  display: none;
}

.externalIdentity-h5 {
  font-size: 1.125em;
  margin: 0.8rem 0;
}
</style>
